import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { humanDate } from '~/libs/utils/format/date';

import { SpinningLoadingIcon } from '~/libs/ui/atoms/SpinningLoadingIcon';
import {
	Notification,
	GenericNotification,
	NotificationPriority,
	Stats,
	Pagination as PaginationData,
} from '~/libs/infrastructure/schemas/';
import {
	getNotifications,
	setNotificationPriority,
	setNotificationRead,
} from '~/libs/infrastructure/api/notifications';
import { PaginationPanel } from '~/libs/ui/molecules/PaginationPanel';
import { NotificationRow } from '~/libs/ui/molecules/NotificationRow';
import { IoCheckmarkDoneSharp, IoFileTrayFullOutline, IoNotificationsOffOutline, IoStarOutline } from 'react-icons/io5';

const MAX_NOTIFICATIONS_COUNT = 20;

export function Notifications() {
	const location = useLocation();
	const history = useHistory();
	const queryParams = new URLSearchParams(location.search);

	const [error, setError] = useState(false);
	const [loadingHeaders, setLoadingHeaders] = useState(true);
	const [loadingNotificationsList, setLoadingNotificationsList] = useState(false);
	const [notificationItems, setNotificationItems] = useState<Notification[]>([]);
	const [groupByDateNotificationItems, setGroupByDateNotificationItems] = useState<
		{ date: string; items: GenericNotification<Date>[] }[]
	>([]);
	const [notificationStats, setNotificationStats] = useState<Stats>();
	const [viewType, setViewType] = useState(queryParams.get('view') || NotificationPriority.NORMAL);
	const [pagination, setPagination] = useState<PaginationData>();
	const [paginationPage, setPagionationPage] = useState(Number(queryParams.get('page')) || 1);

	function fetchNotifications(type: string, page: number) {
		getNotifications(type, page, MAX_NOTIFICATIONS_COUNT)
			.then((data) => {
				setNotificationItems(data.items);
				setDateSortNotifications(data.items);
				setNotificationStats(data.stats);
				setPagination(data.pagination);
				setLoadingHeaders(false);
				setLoadingNotificationsList(false);
			})
			.catch(() => {
				setError(true);
			});
	}

	function setDateSortNotifications(items: GenericNotification<Date>[]) {
		if (!Array.isArray(items)) {
			return;
		}
		const groups = items.reduce(
			(list: { [key: string]: GenericNotification<Date>[] }, item: GenericNotification<Date>) => {
				const itemFormatDate = humanDate(item.createdTime);

				if (!list[itemFormatDate]) {
					list[itemFormatDate] = [];
				}
				list[itemFormatDate].push(item);
				return list;
			},
			{}
		);

		setGroupByDateNotificationItems(
			Object.keys(groups).map((date) => {
				return { date, items: groups[date] };
			})
		);
	}

	const getFormatDate = (date: Date) => {
		const actualDate = new Date();
		const yesterdayDate = new Date(actualDate.setDate(actualDate.getDate() - 1));

		if (humanDate(date) === humanDate(new Date())) {
			return 'Today';
		}
		if (humanDate(date) === humanDate(yesterdayDate)) {
			return 'Yesterday';
		}
		return humanDate(date);
	};

	const updateSearchParams = (type: string, newPaginationPage: number) => {
		fetchNotifications(type, newPaginationPage);

		queryParams.set('view', type);
		queryParams.set('page', newPaginationPage.toString());

		history.push({
			pathname: location.pathname,
			search: queryParams.toString(),
		});
	};

	const onChangeViewType = (type: string) => (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();

		if (viewType == type) {
			return;
		}
		setLoadingNotificationsList(true);
		setViewType(type);
		updateSearchParams(type, 1);
	};

	const onPageChange = (page: number) => {
		setLoadingNotificationsList(true);
		setPagionationPage(page);

		updateSearchParams(viewType, page);
	};

	const onSetRead = (item: string[]) => {
		const today = new Date();
		const backupNotificationsList = notificationItems;
		const notificationList = notificationItems.map((list) =>
			item.indexOf(list.id) > -1
				? { ...list, authenticatedRecipient: { priority: list.authenticatedRecipient.priority, readTime: today } }
				: list
		);
		setNotificationItems(notificationList);
		setDateSortNotifications(notificationList);

		setNotificationRead(item).catch(() => {
			setNotificationItems(backupNotificationsList);
			setDateSortNotifications(backupNotificationsList);
			setError(true);
		});
	};

	const onSetPriority = (item: string[], type: NotificationPriority) => {
		const backupNotificationsList = notificationItems;
		const notificationList = notificationItems.map((list) =>
			list.id === item[0]
				? { ...list, authenticatedRecipient: { priority: type, readTime: list.authenticatedRecipient.readTime } }
				: list
		);
		setNotificationItems(notificationList);
		setDateSortNotifications(notificationList);

		const highPriority = notificationStats?.highPriority ?? 0;
		setNotificationStats({
			highPriority: type == NotificationPriority.HIGH ? highPriority + 1 : highPriority - 1,
			unread: notificationStats?.unread,
		});

		setNotificationPriority(item, type).catch(() => {
			setNotificationItems(backupNotificationsList);
			setDateSortNotifications(backupNotificationsList);
			setError(true);
		});
	};

	useEffect(() => {
		setLoadingHeaders(true);
		fetchNotifications(viewType, paginationPage);
	}, []);

	const renderedGroupNotifications = groupByDateNotificationItems?.map(({ date, items }) => {
		const allDateNotifications: string[] = [];
		let allReaded = true;
		items.map((notification: GenericNotification<Date>) => {
			if (notification.authenticatedRecipient.readTime === null) {
				allReaded = false;
			}
			allDateNotifications.push(notification.id);
		});

		return (
			<div key={date} className='mb-6 px-5'>
				<div className='mb-1.5 flex items-center justify-between'>
					<h3 className='flex items-center justify-start'>
						<strong className='text-h3 font-display font-semibold text-cPurple-600'>
							{getFormatDate(items[0].createdTime)}
						</strong>
						<span className='inline-block ml-3 py-1 px-2.5 rounded-full text-label font-semibold leading-none text-gray-800 bg-gray-200'>
							{items.length}
						</span>
					</h3>
					<a
						href='#'
						className={`inline-flex justify-center items-center pl-1.5 pr-2 h-6 rounded text-label leading-tight font-semibold text-cPurple-800 bg-white ${
							allReaded ? 'opacity-50 pointer-events-none hidden' : 'transition hover:text-white hover:bg-cPurple-800'
						}`}
						onClick={(e: React.MouseEvent<HTMLElement>) => {
							e.stopPropagation();
							onSetRead(allDateNotifications);
						}}
					>
						<IoCheckmarkDoneSharp className='w-4 h-4 mr-1' /> Mark as read
					</a>
				</div>
				<div className='relative p-2 rounded shadow bg-white'>
					{error && (
						<div className='sticky top-2 left-0 w-full h-auto mb-1 px-3 py-2 rounded-md font-semibold text-md text-red-800 bg-red-100 bg-opacity-90 z-20'>
							Application error, please try refreshing the page. If the error persists, please contact us via email
							&nbsp;
							<a
								href='mailto:support@cogvio.com'
								className='font-semibold underline underline-offset-2 text-red-500 transition hover:text-red-800 hover:underline'
							>
								support@cogvio.com
							</a>
						</div>
					)}
					{items.map((notification: GenericNotification<Date>) => {
						return (
							<NotificationRow
								key={notification.id}
								notification={notification}
								showDate={false}
								onSetRead={onSetRead}
								onSetPriority={onSetPriority}
							/>
						);
					})}
				</div>
			</div>
		);
	});

	return (
		<div className='w-full min-h-full bg-gray-100'>
			<div className='bg-white border-b border-gray-200'>
				<div className=' mx-auto' style={{ maxWidth: 1260 }}>
					<div className='relative lg:px-10 px-2 pt-4 z-20'>
						<h2 className='lg:text-perex text-xl font-display font-semibold text-cPurple-800'>Notification</h2>
						<ul className='mt-3 inline-flex justify-start items-end font-semibold text-md leading-tight list-none'>
							<li>
								<a
									href='#'
									className={`relative inline-flex items-center justify-start pl-3 pr-2 py-2 ${
										viewType == NotificationPriority.NORMAL
											? 'text-cPurple-500 cursor-default pointer-events-none'
											: 'text-gray-500 hover:text-cPurple-500 cursor-pointer'
									}`}
									onClick={onChangeViewType(NotificationPriority.NORMAL)}
								>
									<IoFileTrayFullOutline className='relative top-px w-4 h-4 mr-1.5' /> All
									<span className='inline-block ml-2 px-1.5 min-w-5 h-5 rounded-full text-[10px] leading-5 text-gray-500 bg-gray-100 bg-opacity-60'>
										{loadingHeaders ? <SpinningLoadingIcon className='!w-4 !h-4' /> : notificationStats?.unread}
									</span>
									{viewType == NotificationPriority.NORMAL && (
										<i className='absolute left-0 bottom-0 w-full h-0.5 bg-cPurple-500 rounded'></i>
									)}
								</a>
							</li>
							<li>
								<a
									href='#'
									className={`relative inline-flex items-center justify-start pl-3 pr-2 py-2 ${
										viewType == NotificationPriority.HIGH
											? 'text-cPurple-500 cursor-default pointer-events-none'
											: 'text-gray-500 hover:text-cPurple-500 cursor-pointer'
									}`}
									onClick={onChangeViewType(NotificationPriority.HIGH)}
								>
									<IoStarOutline className='w-4 h-4 mr-1.5' /> Priority
									<span className='inline-block ml-2 px-1.5 min-w-5 h-5 rounded-full text-[10px] leading-5 text-gray-500 bg-gray-100 bg-opacity-60'>
										{loadingHeaders ? <SpinningLoadingIcon className='!w-4 !h-4' /> : notificationStats?.highPriority}
									</span>
									{viewType == NotificationPriority.HIGH && (
										<i className='absolute left-0 bottom-0 w-full h-0.5 bg-cPurple-500 rounded'></i>
									)}
								</a>
							</li>
						</ul>
					</div>
				</div>
			</div>
			<div className='mx-auto pb-8' style={{ maxWidth: 1260 }}>
				{!error && loadingHeaders && (
					<div className='py-8 bg-gray-100 flex justify-center'>
						<div className='h-12'>
							<SpinningLoadingIcon />
						</div>
					</div>
				)}
				{error && groupByDateNotificationItems?.length === 0 && (
					<div className='px-8 py-4 w-full'>
						<div className='px-3 py-3 rounded shadow bg-white'>
							<div className='w-full h-auto px-3 py-2 rounded-md font-semibold text-md text-red-800 bg-red-100 bg-opacity-90 z-20'>
								Application error, please try refreshing the page. If the error persists, please contact us via email
								&nbsp;
								<a
									href='mailto:support@cogvio.com'
									className='font-semibold underline underline-offset-2 text-red-500 transition hover:text-red-800 hover:underline'
								>
									support@cogvio.com
								</a>
							</div>
						</div>
					</div>
				)}
				<div className='lg:px-5 xl:pt-7 pt-5'>
					{!loadingHeaders && groupByDateNotificationItems?.length === 0 && (
						<div className='flex items-center justify-center py-2 w-full rounded text-gray-800 font-semibold text-md bg-white text-center shadow'>
							<IoNotificationsOffOutline className='relative -mt-px w-6 h-6 mr-1' /> You have no notifications
						</div>
					)}
					{!loadingHeaders && (
						<div className='relative'>
							<div className={`${loadingNotificationsList ? 'opacity-20' : ''}`}>{renderedGroupNotifications}</div>
							{loadingNotificationsList && (
								<div className='absolute top-0 left-0 w-full h-full py-14 flex justify-center items-center'>
									<div className='h-12'>
										<SpinningLoadingIcon />
									</div>
								</div>
							)}
						</div>
					)}
					{pagination && (
						<div className='w-full px-5 flex justify-start items-start'>
							<PaginationPanel pagination={pagination} onChange={onPageChange} />
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
